import React from "react";

/** Cinematic embers + light shaft backdrop. Decorative only. */
export const EmbersBackdrop = ({ density = 14 }) => {
  // Generate ember positions ONCE per mount (keeps render pure)
  const [embers] = React.useState(() =>
    Array.from({ length: density }).map(() => ({
      key: Math.random().toString(36).slice(2),
      left: Math.random() * 100,
      delay: Math.random() * 12,
      duration: 12 + Math.random() * 18,
      size: 2 + Math.random() * 3,
      bottomOffset: Math.random() * 20,
    }))
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="light-shaft" />
      {embers.map((e) => (
        <span
          key={e.key}
          className="ember"
          style={{
            left: `${e.left}%`,
            bottom: `-${e.bottomOffset}px`,
            animationDelay: `${e.delay}s`,
            animationDuration: `${e.duration}s`,
            width: `${e.size}px`,
            height: `${e.size}px`,
          }}
        />
      ))}
    </div>
  );
};

export const GoldDivider = ({ className = "" }) => (
  <div className={`gold-divider ${className}`} aria-hidden="true" />
);

export const SectionHeading = ({ overline, title, subtitle, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} mb-14`}>
    {overline && <div className="overline mb-4" data-testid="section-overline">{overline}</div>}
    <h2
      className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F6F0E6]"
      data-testid="section-title"
    >
      {title}
    </h2>
    {subtitle && (
      <p className="mt-5 text-[#D9D9D9] text-base sm:text-lg leading-relaxed max-w-2xl">
        {subtitle}
      </p>
    )}
  </div>
);

/** Reveal-on-scroll wrapper using IntersectionObserver */
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add("visible"), delay);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

/** Subtle 3D tilt card */
export const TiltCard = ({ children, className = "", strength = 6, ...rest }) => {
  const ref = React.useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-y * strength}deg) rotateY(${x * strength}deg) translateZ(0)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className={`tilt ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
};
